import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { serialQueue, writeDayRow } from '../lib/dayRowCas';
import { refreshTodayOnFocus } from '../lib/dayLogFocusRefresh';
import { onSignOut } from '../lib/signOutReset';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import {
  type FoodEntry,
  type Meal,
  type MealSource,
  type NutritionDay,
  type NutritionTargets,
  type Nutrients,
  type OccasionType,
  NO_TARGETS,
  NUTRIENT_KEYS,
  DEFAULT_PRESET_ICON,
  canLogOn,
  readNutrients,
  readOccasionTags,
  scaleNutrients,
  sumNutritionXP,
  nutritionStreak,
} from '../utils/nutrition';
import { toLocalYMD, shiftYMD } from '../utils/hydration';
import { readRecipe, type Recipe } from '../utils/mealPrep';
import { deleteMealPhoto } from '../utils/mediaUpload';
import {
  type CalorieSource, type NutritionDirection, type TargetVersion,
  currentVersion, readDirection, versionTargets,
} from '../utils/nutritionTargets';

/** Days of history loaded — and the furthest back a past-day log may go. */
export const HISTORY_DAYS = 60;

export const SAVE_ERROR = 'Couldn’t save — check your connection and try again. Nothing was changed.';
export const MEALS_LOAD_ERROR = 'Couldn’t load your meals. Check your connection and try again.';

/** Every writer resolves to this: `ok` only when the database accepted the change. */
export type WriteResult<T = undefined> = { ok: true; value: T } | { ok: false; error: string };

interface NutritionRow {
  log_date: string;
  entries: FoodEntry[] | null;
  updated_at: string;
}

interface MealRow {
  id: string;
  revision: number;
  name: string;
  serving_label: string;
  serving_grams: number | string | null;
  nutrients: unknown;
  nutrient_source: MealSource;
  icon: string | null;
  legacy_preset_id: string | null;
  /** Phase 4 (mig 0293); absent before it runs. */
  recipe?: unknown;
  /** Mig 0298; absent before it runs. */
  occasion_tags?: string[] | null;
  /** Mig 0301; absent before it runs. */
  photo_path?: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** What the meal editor hands the store (a new meal or an edit). */
export interface MealDraft {
  name: string;
  servingLabel: string;
  servingGrams: number | null;
  nutrients: Nutrients;
  source: MealSource;
  icon: string;
  /** Phase 4: the optional recipe — undefined = leave it as it is; null = no recipe. */
  recipe?: Recipe | null;
  /** What it's for — undefined = leave as is (the editor always sends them). */
  occasionTags?: OccasionType[];
  /** Its photo's path — undefined = leave as is; null = no photo. */
  photoPath?: string | null;
}

/** A confirmed choice of targets — numbers (each optional) or "no numbers". */
export type TargetsDraft =
  | { kind: 'none' }
  | {
      kind: 'numbers';
      calories: number | null; protein: number | null; carbs: number | null; fat: number | null;
      /** Required when calories are set. */
      calorieSource: CalorieSource | null;
      method: string | null;
      inputs: Record<string, unknown>;
    };

interface TargetVersionRow {
  id: string; kind: 'numbers' | 'none';
  calories: number | null; protein: number | null; carbs: number | null; fat: number | null;
  calorie_source: CalorieSource | null; method: string | null; inputs: Record<string, unknown> | null;
  confirmed_at: string; effective_from: string;
}

export function rowToVersion(r: TargetVersionRow): TargetVersion {
  return {
    id: r.id, kind: r.kind, calories: r.calories, protein: r.protein, carbs: r.carbs, fat: r.fat,
    calorieSource: r.calorie_source, method: r.method, inputs: r.inputs ?? {},
    confirmedAt: r.confirmed_at, effectiveFrom: r.effective_from,
  };
}

/** One food to log, as eaten. From a meal: pass `meal` + `portion`; a one-off: `name` + `nutrients`. */
/** `occasionId`: the planned occasion this fulfils (phase 3a), stamped on the entry. */
export type LogInput =
  | { meal: Meal; portion: number; occasionId?: string }
  | { name: string; nutrients: Nutrients; occasionId?: string };

function rowToDay(row: NutritionRow): NutritionDay {
  return { date: row.log_date, entries: Array.isArray(row.entries) ? row.entries : [] };
}

export function rowToMeal(row: MealRow): Meal {
  const grams = row.serving_grams === null ? null : Number(row.serving_grams);
  return {
    id: row.id,
    revision: row.revision,
    name: row.name,
    servingLabel: row.serving_label,
    servingGrams: grams !== null && Number.isFinite(grams) && grams > 0 ? grams : null,
    nutrients: readNutrients(row.nutrients),
    source: row.nutrient_source,
    icon: row.icon || DEFAULT_PRESET_ICON,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    legacyPresetId: row.legacy_preset_id,
    recipe: readRecipe(row.recipe),
    occasionTags: readOccasionTags(row.occasion_tags),
    photoPath: typeof row.photo_path === 'string' && row.photo_path ? row.photo_path : null,
  };
}

/** The jsonb we store: only known values (an absent key reads back as unknown). */
function nutrientsToJson(n: Nutrients): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of NUTRIENT_KEYS) if (typeof n[k] === 'number') out[k] = n[k] as number;
  return out;
}

/**
 * ⚠️ `recipe` is sent only when there is one to write or clear — so editing a meal
 * that never had a recipe works the same before migration 0293 adds the column.
 */
function mealDraftToRow(d: MealDraft, had: Recipe | null = null) {
  const recipe = d.recipe === undefined ? undefined : d.recipe ? readRecipe(d.recipe) : null;
  return {
    name: d.name.trim().slice(0, 120),
    serving_label: d.servingLabel.trim().slice(0, 60),
    serving_grams: d.servingGrams,
    nutrients: nutrientsToJson(d.nutrients),
    nutrient_source: d.source,
    icon: d.icon || DEFAULT_PRESET_ICON,
    ...(recipe !== undefined && (recipe || had) ? { recipe } : {}),
    // Mig 0298: sent only when given (a draft from elsewhere leaves the tags as they are).
    ...(d.occasionTags !== undefined ? { occasion_tags: readOccasionTags(d.occasionTags) } : {}),
    // Mig 0301: sent only when the photo changes.
    ...(d.photoPath !== undefined ? { photo_path: d.photoPath } : {}),
  };
}

/** Nutrients, serving or recipe changed → a new revision (logged entries and prepared batches keep theirs). */
export function mealChangesRevision(before: Meal, d: MealDraft): boolean {
  if (before.servingLabel.trim() !== d.servingLabel.trim()) return true;
  if (d.recipe !== undefined && JSON.stringify(before.recipe ?? null) !== JSON.stringify(d.recipe ? readRecipe(d.recipe) : null)) return true;
  if ((before.servingGrams ?? null) !== (d.servingGrams ?? null)) return true;
  return NUTRIENT_KEYS.some(k => (before.nutrients[k] ?? null) !== (d.nutrients[k] ?? null));
}

/** Build the entry a log creates. From a meal: snapshot + the portion's nutrients. */
export function entryFromInput(input: LogInput, at: string, id: string = crypto.randomUUID()): FoodEntry {
  if ('meal' in input) {
    const { meal, portion } = input;
    const eaten = scaleNutrients(meal.nutrients, portion);
    return {
      id, name: meal.name, at, ...eaten,
      mealId: meal.id, mealRevision: meal.revision, portion,
      snapshot: { name: meal.name, servingLabel: meal.servingLabel, servingGrams: meal.servingGrams, nutrients: { ...meal.nutrients }, source: meal.source },
      ...(input.occasionId ? { occasionId: input.occasionId } : {}),
    };
  }
  return { id, name: input.name.trim().slice(0, 120) || 'Food', at, ...input.nutrients, ...(input.occasionId ? { occasionId: input.occasionId } : {}) };
}

/** An entry with a new portion — recomputed from its FROZEN snapshot, never the live meal. */
export function withPortion(e: FoodEntry, portion: number): FoodEntry {
  if (!e.snapshot) return e;
  return { ...e, portion, ...scaleNutrients(e.snapshot.nutrients, portion) };
}

function withDay(history: NutritionDay[], day: NutritionDay): NutritionDay[] {
  const others = history.filter(d => d.date !== day.date);
  return [...others, day].sort((a, b) => a.date.localeCompare(b.date));
}

function entriesOf(history: NutritionDay[], date: string): FoodEntry[] {
  return history.find(d => d.date === date)?.entries ?? [];
}

/** An entry with an edit applied — to whichever copy of it is current (the server's, inside a write). */
function patchEntry(e: FoodEntry, patch: { portion?: number; nutrients?: Nutrients; name?: string }): FoodEntry {
  let after: FoodEntry = { ...e };
  if (patch.portion !== undefined) after = withPortion(after, patch.portion);
  if (patch.nutrients) after = { ...after, ...patch.nutrients };
  if (patch.name !== undefined && !e.snapshot) after = { ...after, name: patch.name.trim().slice(0, 120) || e.name };
  return after;
}

/**
 * ⚠️⚠️ ONE WRITE AT A TIME, AND EACH ONE A CHANGE — NEVER THE DAY (docs/DAY_LOG_STALE_WRITES.md).
 * A day is one row, so a save used to send the whole day from this tab's memory: a
 * stale tab brought back food removed on another device, or dropped food added there.
 * Now each queued save reads the day from the server, applies its ONE change there
 * (add / edit / remove an entry by id) and writes with compare-and-swap. If a save
 * fails its own change is reverted on screen; on success the screen takes the
 * server's day, so a stale tab catches up on the day it wrote to. The load runs in
 * the same queue, so it never overwrites a write made while it was reading — and so
 * does `refreshToday` (the tab came back), which re-reads today only.
 */
let enqueue = serialQueue();
let loading: Promise<void> | null = null;
/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store — and a day write
 * still waiting its turn is never sent once that account has left (it asks who is
 * signed in only when its turn comes, so it would be sent for the NEXT account).
 */
let epoch = 0;
const focus = refreshTodayOnFocus(() => useNutritionStore.getState().refreshToday());

const rowEntries = (row: NutritionRow | null): FoodEntry[] => (row && Array.isArray(row.entries) ? row.entries : []);

interface NutritionStore {
  todayDate: string;
  /** Today's entries (kept in step with `history`; read by Health and older screens). */
  todayEntries: FoodEntry[];
  history: NutritionDay[];
  /** The numbers in effect today (from the current version; none = no numbers). */
  targets: NutritionTargets;
  /** Every confirmed version, oldest first (insert-only history). */
  targetVersions: TargetVersion[];
  /** The versions read failed (e.g. the table is not there yet) — targets fall back to the old columns. */
  targetsFailed: boolean;
  /** Intention, restrictions, constraints, tracking preference, activity (null = never saved). */
  direction: NutritionDirection | null;
  /** Food with no linked habit may be logged on past days (owner Q2). */
  allowPastDays: boolean;
  meals: Meal[];
  /** The meals read failed (e.g. the table is not there yet) — never shown as "no meals". */
  mealsFailed: boolean;
  /**
   * ⭐ The meals read has ANSWERED — ok or failed (`mealsFailed` says which) (step 5B, 2026-10-22).
   * ⚠️ `isInitialized` turns true with the day logs, BEFORE the meals and the target versions
   * arrive: a screen that names a planned meal or claims "no calorie target" waits for these.
   */
  mealsLoaded: boolean;
  /** ⭐ The target versions read has ANSWERED — ok or failed (`targetsFailed`) (step 5B). */
  targetsLoaded: boolean;

  /** Any food ever logged (Foundation step 8, phase 3c) — the loaded days, else older rows; null = can't tell. */
  everLogged: boolean | null;
  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;
  /** The day logs read failed — Today says so instead of showing an empty day. */
  loadFailed: boolean;

  initialize: () => Promise<void>;
  /** Re-read today's row (the tab came back — lib/dayLogFocusRefresh). Read only. */
  refreshToday: () => Promise<void>;
  reloadMeals: () => Promise<void>;
  entriesFor: (date: string) => FoodEntry[];
  /** The oldest day a past-day log may use (the loaded window). */
  oldestLoggable: () => string;
  canLog: (date: string) => boolean;

  /**
   * `opts.opId` (phase 3b-2): the linked eat-and-tick operation — an entry with that id
   * already on the day is returned as is (a retry never adds a second). `opts.habitAllowsDate`:
   * the linked habit's own back-dating rule already allowed this day (D4) — only the
   * future and days older than the loaded history are refused then.
   */
  logFood: (date: string, input: LogInput, opts?: { opId?: string; habitAllowsDate?: boolean }) => Promise<WriteResult<FoodEntry>>;
  updateEntry: (date: string, id: string, patch: { portion?: number; nutrients?: Nutrients; name?: string }) => Promise<WriteResult>;
  removeEntry: (date: string, id: string) => Promise<WriteResult<{ entry: FoodEntry; index: number }>>;
  /** Undo a removal: the same entry back in the same place. */
  restoreEntry: (date: string, entry: FoodEntry, index: number) => Promise<WriteResult>;

  createMeal: (draft: MealDraft) => Promise<WriteResult<Meal>>;
  updateMeal: (id: string, draft: MealDraft) => Promise<WriteResult<Meal>>;
  /** ⭐ Delete a meal for good (owner, 2026-10-18 — replaces Archive). See the implementation. */
  deleteMeal: (id: string) => Promise<WriteResult>;

  /** Confirm a choice: a NEW version, effective today (history is never edited). */
  confirmTargets: (draft: TargetsDraft) => Promise<WriteResult<TargetVersion>>;
  setDirection: (d: NutritionDirection) => Promise<WriteResult>;
  reloadTargets: () => Promise<void>;
  setAllowPastDays: (allow: boolean) => Promise<WriteResult>;

  getNutritionXP: () => number;
  getStreak: () => number;
  clearStore: () => void;
}

const fail = (error = SAVE_ERROR) => ({ ok: false as const, error });

export const useNutritionStore = create<NutritionStore>((set, get) => {
  /** Replace one day's entries (and today's mirror). */
  const setDay = (date: string, entries: FoodEntry[]) => {
    const history = withDay(get().history, { date, entries });
    const today = toLocalYMD(new Date());
    set({ history, todayDate: today, todayEntries: entriesOf(history, today) });
  };

  /**
   * Apply a change to a day, save it, and on failure undo ONLY that change. `apply`
   * runs on the SERVER's entries inside the write (and on the screen's, at once); it
   * returns the same array when there's nothing to change, and then nothing is written.
   */
  const writeDay = (date: string, apply: (e: FoodEntry[]) => FoodEntry[], revert: (e: FoodEntry[]) => FoodEntry[], at = epoch) =>
    enqueue(async () => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user || at !== epoch) return false;
      setDay(date, apply(entriesOf(get().history, date)));
      const res = await writeDayRow<NutritionRow>(
        { table: 'nutrition_logs', userId: user.id, date, columns: 'log_date, entries' },
        (row) => {
          const before = rowEntries(row);
          const after = apply(before);
          return after === before ? null : { entries: after };
        },
      );
      if (at !== epoch) return res.ok; // signed out while it saved: not shown to the next account
      if (!res.ok) {
        console.error('Error saving nutrition log:', res.error);
        setDay(date, revert(entriesOf(get().history, date)));
        return false;
      }
      setDay(date, rowEntries(res.row));
      return true;
    });

  /**
   * Your meals, as saved. ⚠️ (2026-10-19) There used to be a one-time move of saved quick-add
   * presets into meals here, and it ran on EVERY load: once Delete replaced Archive (os#122),
   * a deleted preset meal had no row left, so the next load read its preset as "never moved"
   * and created it again. Every saved preset had been moved by then (checked on the live
   * database: 1 account, 5 presets, 0 waiting), so the move is retired rather than patched.
   */
  const loadMeals = async (userId: string, at: number): Promise<boolean> => {
    const { data, error } = await supabase.from('nutrition_meals').select('*').eq('user_id', userId).order('created_at', { ascending: true });
    if (error) {
      console.error('Error loading meals:', error);
      if (at === epoch) set({ mealsFailed: true, mealsLoaded: true }); // signed out meanwhile: not the next account's flag
      return false;
    }
    // Signed out while it read: the next account must not get this one's meals.
    if (at !== epoch) return false;
    set({ meals: (data as MealRow[] ?? []).map(rowToMeal), mealsFailed: false, mealsLoaded: true });
    return true;
  };

  /** Read the versions; on failure keep the old columns' numbers and say so. */
  const loadVersions = async (userId: string, legacy: NutritionTargets, at: number): Promise<void> => {
    const { data, error } = await supabase
      .from('nutrition_target_versions')
      .select('*')
      .eq('user_id', userId)
      .order('effective_from', { ascending: true })
      .order('confirmed_at', { ascending: true });
    if (at !== epoch) return; // signed out while it read
    if (error) {
      console.error('Error loading target versions:', error);
      set({ targetVersions: [], targetsFailed: true, targets: legacy, targetsLoaded: true });
      return;
    }
    const versions = (data as TargetVersionRow[] ?? []).map(rowToVersion);
    const current = currentVersion(versions, toLocalYMD(new Date()));
    // ⚠️ No version yet = the old columns (an account the migration found nothing for has
    // none there either — both read "No targets set").
    set({ targetVersions: versions, targetsFailed: false, targets: current ? versionTargets(current) : legacy, targetsLoaded: true });
  };

  const load = async (): Promise<void> => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    if (get().initializedUserId === user.id && !get().loadFailed) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();
      const row = prefs?.row;
      const num = (v: unknown) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
      const targets: NutritionTargets = {
        calories: num(row?.nutrition_calorie_target),
        protein: num(row?.nutrition_protein_target),
        carbs: num(row?.nutrition_carbs_target),
        fat: num(row?.nutrition_fat_target),
      };

      const todayStr = toLocalYMD(new Date());
      const since = shiftYMD(todayStr, -HISTORY_DAYS);
      const { data, error } = await supabase
        .from('nutrition_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      if (error) throw error;
      const history = ((data ?? []) as NutritionRow[]).map(rowToDay);
      // Ever logged? The loaded days answer it; if they're empty, look further back
      // (a short, bounded read). A failed read is "can't tell", never "no".
      let everLogged: boolean | null = history.some(d => d.entries.length > 0) ? true : null;
      if (everLogged === null) {
        const OLDER = 30;
        try {
          const older = await supabase.from('nutrition_logs').select('entries').eq('user_id', user.id).lt('log_date', since).order('log_date', { ascending: false }).limit(OLDER);
          if (!older.error) {
            const rows = (older.data ?? []) as { entries: unknown }[];
            everLogged = rows.some(r => Array.isArray(r.entries) && r.entries.length > 0) ? true : rows.length < OLDER ? false : null;
          }
        } catch (err) {
          // ⚠️ Its own failure — "can't tell" for this one answer, never a failed load.
          console.error('Error checking older nutrition logs:', err);
        }
      }
      // Signed out while reading: the next account must not get this one's food.
      if (at !== epoch) return;

      set({
        targets,
        direction: readDirection(row?.nutrition_direction),
        allowPastDays: row?.nutrition_allow_past_days === true,
        todayDate: todayStr,
        history,
        everLogged,
        todayEntries: entriesOf(history, todayStr),
        isLoading: false,
        isInitialized: true,
        initializedUserId: user.id,
        loadFailed: !!prefs?.failed,
      });
      focus.read(todayStr);
      await Promise.all([loadMeals(user.id, at), loadVersions(user.id, targets, at)]);
    } catch (err) {
      if (at !== epoch) return; // the next account's load may be running: leave its flags alone
      console.error('Error initializing nutrition:', err);
      set({ isLoading: false, loadFailed: true });
    }
  };

  /**
   * ⚠️ Read only, and in the write queue: a save puts its change on screen only when its
   * turn comes, so when this runs nothing is half-saved — the server's day IS the day.
   */
  const refreshToday = () => enqueue(async () => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || get().initializedUserId !== user.id) return;
    // The calendar's today — a tab woken after midnight moves on to the new day.
    const date = toLocalYMD(new Date());
    const { data, error } = await supabase
      .from('nutrition_logs')
      .select('log_date, entries, updated_at')
      .eq('user_id', user.id)
      .eq('log_date', date)
      .maybeSingle();
    if (error) { console.error('Error refreshing nutrition:', error); return; }
    if (get().initializedUserId !== user.id) return;        // signed out while it read
    const entries = rowEntries((data ?? null) as NutritionRow | null);
    setDay(date, entries);
    if (entries.length) set({ everLogged: true });
    focus.read(date);
  });

  return {
    todayDate: toLocalYMD(new Date()),
    todayEntries: [],
    history: [],
    targets: { ...NO_TARGETS },
    targetVersions: [],
    targetsFailed: false,
    direction: null,
    allowPastDays: false,
    meals: [],
    mealsFailed: false,
    mealsLoaded: false,
    targetsLoaded: false,
    everLogged: null,

    isLoading: false,
    isInitialized: false,
    initializedUserId: null,
    loadFailed: false,

    /* ⚠️ A call while loading JOINS that load — it used to return at once, so a caller
       that awaited it went on to act on a day that wasn't loaded yet. */
    initialize: () => {
      if (loading) return loading;
      // ⚠️ Clears only its own: a sign-out drops this load and the next account starts its own.
      const mine: Promise<void> = enqueue(() => load()).finally(() => { if (loading === mine) loading = null; });
      loading = mine;
      return mine;
    },

    refreshToday,

    reloadMeals: async () => {
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      await loadMeals(user.id, at);
    },


    entriesFor: (date) => entriesOf(get().history, date),
    oldestLoggable: () => shiftYMD(toLocalYMD(new Date()), -HISTORY_DAYS),
    canLog: (date) => canLogOn(date, toLocalYMD(new Date()), get().allowPastDays, get().oldestLoggable()),

    logFood: async (date, input, opts) => {
      const at = epoch;
      const today = toLocalYMD(new Date());
      const allowed = opts?.habitAllowsDate
        ? date <= today && date >= get().oldestLoggable()
        : get().canLog(date);
      if (!allowed) return fail('That day can’t be logged — turn on “Allow logging past days” to log earlier days.');
      if ('meal' in input && !(input.portion > 0)) return fail('Choose a portion above zero.');
      if (opts?.opId) {
        const existing = entriesOf(get().history, date).find(e => e.opId === opts.opId);
        if (existing) return { ok: true, value: existing };
      }
      const entry = { ...entryFromInput(input, new Date().toISOString()), ...(opts?.opId ? { opId: opts.opId } : {}) };
      // ⚠️ The duplicate check runs again INSIDE the queued write, on the SERVER's day: a
      // second call with the same operation — made while the first was saving, or on
      // another device — finds it there.
      const ok = await writeDay(
        date,
        e => ((opts?.opId && e.some(x => x.opId === opts.opId)) || e.some(x => x.id === entry.id) ? e : [...e, entry]),
        e => e.filter(x => x.id !== entry.id),
      );
      if (!ok) return fail();
      if (at === epoch) set({ everLogged: true });
      const saved = opts?.opId ? entriesOf(get().history, date).find(e => e.opId === opts.opId) : entry;
      return { ok: true, value: saved ?? entry };
    },

    updateEntry: async (date, id, patch) => {
      const before = entriesOf(get().history, date).find(e => e.id === id);
      if (!before) return fail('That entry is no longer there.');
      if (patch.portion !== undefined && !(patch.portion > 0)) return fail('Choose a portion above zero.');
      // ⚠️ The edit is applied to the SERVER's copy of the entry (another device's edit to
      // another field survives). Removed elsewhere → it is NOT written back: the entry
      // leaves this screen too, and the caller is told.
      let gone = false;
      const ok = await writeDay(
        date,
        e => {
          const cur = e.find(x => x.id === id);
          gone = !cur;
          return cur ? e.map(x => (x.id === id ? patchEntry(cur, patch) : x)) : e;
        },
        e => e.map(x => (x.id === id ? before : x)),
      );
      if (ok && gone) return fail('That entry is no longer there.');
      return ok ? { ok: true, value: undefined } : fail();
    },

    removeEntry: async (date, id) => {
      const list = entriesOf(get().history, date);
      const index = list.findIndex(e => e.id === id);
      if (index < 0) return fail('That entry is no longer there.');
      const entry = list[index];
      const ok = await writeDay(
        date,
        e => (e.some(x => x.id === id) ? e.filter(x => x.id !== id) : e),
        e => (e.some(x => x.id === id) ? e : [...e.slice(0, index), entry, ...e.slice(index)]),
      );
      return ok ? { ok: true, value: { entry, index } } : fail();
    },

    restoreEntry: async (date, entry, index) => {
      const ok = await writeDay(
        date,
        e => (e.some(x => x.id === entry.id) ? e : [...e.slice(0, index), entry, ...e.slice(index)]),
        e => e.filter(x => x.id !== entry.id),
      );
      return ok ? { ok: true, value: undefined } : fail();
    },

    createMeal: async (draft) => {
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      if (!draft.name.trim() || !draft.servingLabel.trim()) return fail('A meal needs a name and a serving.');
      const { data, error } = await supabase
        .from('nutrition_meals')
        .insert({ user_id: user.id, revision: 1, ...mealDraftToRow(draft) })
        .select('*')
        .single();
      if (error || !data) { console.error('Error creating meal:', error); return fail(); }
      const meal = rowToMeal(data as MealRow);
      if (at === epoch) set({ meals: [...get().meals, meal] }); // signed out meanwhile: not the next account's
      return { ok: true, value: meal };
    },

    /* Edit and delete only touch a meal already here by id — a sign-out empties `meals`,
       so an answer after it finds nothing to change. */
    updateMeal: async (id, draft) => {
      const before = get().meals.find(m => m.id === id);
      if (!before) return fail('That meal is no longer there.');
      if (!draft.name.trim() || !draft.servingLabel.trim()) return fail('A meal needs a name and a serving.');
      const revision = mealChangesRevision(before, draft) ? before.revision + 1 : before.revision;
      const { data, error } = await supabase
        .from('nutrition_meals')
        .update({ ...mealDraftToRow(draft, before.recipe ?? null), revision, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
      if (error || !data) { console.error('Error updating meal:', error); return fail(); }
      const meal = rowToMeal(data as MealRow);
      set({ meals: get().meals.map(m => (m.id === id ? meal : m)) });
      return { ok: true, value: meal };
    },

    /* ⚠️ Its plan cells, its places in plans' meal lists and its Meal Prep batches go with it
       (foreign keys, ON DELETE CASCADE) — the caller reloads those stores. Logged food keeps its
       own copy (an entry holds a snapshot of the meal, not a reference), so history never changes.
       ⚠️ A refusal under RLS deletes nothing and returns no row: that is a failure, not success. */
    deleteMeal: async (id) => {
      const before = get().meals.find(m => m.id === id);
      if (!before) return fail('That meal is no longer there.');
      const { data, error } = await supabase.from('nutrition_meals').delete().eq('id', id).select('id');
      if (error) { console.error('Error deleting meal:', error); return fail(); }
      if (!data?.length) return fail('Couldn’t delete it — it may already be gone. Reload to see.');
      set({ meals: get().meals.filter(m => m.id !== id) });
      // Its photo goes too — only after the row, so a refused delete never loses the picture.
      if (before.photoPath) void deleteMealPhoto(before.photoPath);
      return { ok: true, value: undefined };
    },

    confirmTargets: async (draft) => {
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const row = draft.kind === 'none'
        ? { kind: 'none', calories: null, protein: null, carbs: null, fat: null, calorie_source: null, method: null, inputs: {} }
        : {
            kind: 'numbers', calories: draft.calories, protein: draft.protein, carbs: draft.carbs, fat: draft.fat,
            calorie_source: draft.calories === null ? null : draft.calorieSource, method: draft.method, inputs: draft.inputs,
          };
      if (row.kind === 'numbers' && [row.calories, row.protein, row.carbs, row.fat].every(v => v === null)) {
        return fail('Set at least one number — or choose “Track without numbers”.');
      }
      if (row.calories !== null && !row.calorie_source) return fail('Say where the calories came from.');
      const { data, error } = await supabase
        .from('nutrition_target_versions')
        .insert({ user_id: user.id, ...row, effective_from: toLocalYMD(new Date()) })
        .select('*')
        .single();
      if (error || !data) { console.error('Error saving targets:', error); return fail(); }
      const version = rowToVersion(data as TargetVersionRow);
      // Signed out meanwhile: saved for that account — not shown to the next, and never
      // mirrored onto ITS columns (the mirror writes as whoever is signed in now).
      if (at !== epoch) return { ok: true, value: version };
      const versions = [...get().targetVersions, version];
      const targets = versionTargets(currentVersion(versions, toLocalYMD(new Date())));
      set({ targetVersions: versions, targets, targetsFailed: false });
      // Mirror onto the four old columns so older app builds read the same numbers. The
      // VERSION is the truth; a failed mirror only delays an old build, so it doesn't undo it.
      void saveUserPreference({
        nutrition_calorie_target: targets.calories,
        nutrition_protein_target: targets.protein,
        nutrition_carbs_target: targets.carbs,
        nutrition_fat_target: targets.fat,
      }).then(ok => { if (!ok) console.error('Targets saved as a version; mirroring to the old columns failed.'); });
      return { ok: true, value: version };
    },

    setDirection: async (d) => {
      const at = epoch;
      const before = get().direction;
      const next = { ...d, version: 1 as const, updatedAt: new Date().toISOString() };
      set({ direction: next });
      const ok = await saveUserPreference({ nutrition_direction: next });
      // Refused after a sign-out: the next account's store keeps its own (not this one's old direction).
      if (!ok) { if (at === epoch) set({ direction: before }); return fail(); }
      return { ok: true, value: undefined };
    },

    reloadTargets: async () => {
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      await loadVersions(user.id, get().targets, at);
    },

    setAllowPastDays: async (allow) => {
      const at = epoch;
      const before = get().allowPastDays;
      set({ allowPastDays: allow });
      const ok = await saveUserPreference({ nutrition_allow_past_days: allow });
      if (!ok) { if (at === epoch) set({ allowPastDays: before }); return fail(); }
      return { ok: true, value: undefined };
    },

    getNutritionXP: () => sumNutritionXP(get().history),
    getStreak: () => nutritionStreak(get().history, toLocalYMD(new Date()), shiftYMD),

    /**
     * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
     * not reload the page: without this the food log, meals and targets stayed in memory
     * for the next account. The work queue, the load in flight and the focus refresh go too.
     */
    clearStore: () => {
      epoch += 1;
      // A fresh queue: the next account's load never waits behind the old account's work.
      enqueue = serialQueue();
      loading = null;
      focus.forget();
      set({
        todayDate: toLocalYMD(new Date()),
        todayEntries: [],
        history: [],
        targets: { ...NO_TARGETS },
        targetVersions: [],
        targetsFailed: false,
        direction: null,
        allowPastDays: false,
        meals: [],
        mealsFailed: false,
        mealsLoaded: false,
        targetsLoaded: false,
        everLogged: null,
        isLoading: false,
        isInitialized: false,
        initializedUserId: null,
        loadFailed: false,
      });
    },
  };
});

onSignOut(() => useNutritionStore.getState().clearStore());
